import {
  ReactNode,
  PropsWithChildren,
  useState,
  useEffect,
  useRef,
  useCallback,
  createContext,
  useContext,
} from "react";
import Twemoji from "react-twemoji";

import { ScopeType } from "sockapi/scope-types";
import { AdminQueueTaskSummary } from "sockapi/scope-specs/admin-queue-spec";
import { AdminQueueTaskType } from "sockapi/scope-specs/admin-queue-task-spec";
import { HintResponseType, HintStrength } from "sockapi/scope-specs/hint-spec";
import { PuzzleContentType } from "sockapi/scope-specs/puzzle-content-spec";
import {
  useScopeStateResolvable,
  useGlobalScopeState,
  useTeamAnswerableScopeState,
  useSortedHintsForTeamPuzzle,
  useNumTotalHintTokens,
  useNumUsedHintTokens,
  usePuzzleDisplayName,
} from "stores/ScopeStateStore";
import { useSockClient } from "stores/ServerInteractionStore";
import { useNavStore } from "stores/NavStore";
import { Approachability } from "components/Approachability";
import { Spinner } from "components/Loading";
import { PuzzleLink } from "components/Link";
import { ButtonTabs } from "components/AdminQueue/ButtonTabs";
import HintDisplay, {
  HINT_STRENGTH_ICON,
} from "components/PuzzlePage/HintDisplay";
import { AdminRelatedPuzzleControls } from "components/PuzzlePage/PuzzleBodyDisplay";
import PuzzleContentDisplay from "components/PuzzleContent/PuzzleContentDisplay";
import PreviousGuessesDisplay from "components/PuzzlePage/PreviousGuessesDisplay";
import AdminQueueTaskListDisplay from "components/AdminQueue/AdminQueueTaskListDisplay";
import TeamDisplayName from "components/TeamDisplayName";
import { AdminNotes } from "pages/AdminPage";

function TeamInfo({ teamId }: { teamId: string }) {
  const teamHintsScopeState = useScopeStateResolvable(
    ScopeType.TEAM_HINTS,
    teamId === null ? null : { teamId }
  );
  const numTotalHintTokens = useNumTotalHintTokens(teamId);
  const numUsedHintTokens = useNumUsedHintTokens(teamId);

  if (
    teamHintsScopeState === null ||
    numTotalHintTokens === null ||
    numUsedHintTokens === null
  )
    return <Spinner inline />;

  const getCount = (key: "followup" | HintStrength) => {
    if (teamHintsScopeState.categoryCounts === undefined) return "?";
    return teamHintsScopeState.categoryCounts[key] ?? 0;
  };

  return (
    <>
      <div>
        Team: <TeamDisplayName teamId={teamId} /> <AdminNotes teamId={teamId} />
      </div>
      <div>
        Hint tokens: {numTotalHintTokens} total, {numUsedHintTokens} spent +{" "}
        {getCount("followup")} followup
        {Object.values(HintStrength).map((strength) => (
          <Twemoji
            key={strength}
            tag="span"
          >{`, ${getCount(strength)} ${HINT_STRENGTH_ICON[strength]}`}</Twemoji>
        ))}
      </div>
    </>
  );
}

export function HintAdminQueueTaskSummaryDisplay(props: { hintId: string }) {
  const { hintId } = props;
  const hintScopeState = useScopeStateResolvable(ScopeType.HINT, { hintId });
  const { puzName = null, teamId = null } = hintScopeState ?? {};
  const displayName = usePuzzleDisplayName(puzName, teamId);
  const huntAdminScopeState = useGlobalScopeState(ScopeType.HUNT_ADMIN);
  const hasAnswer =
    (puzName !== null &&
      (huntAdminScopeState?.puzzles[puzName].answer ?? null) !== null) ??
    false;
  // Don't try to sync an answerable state if the puzzle doesn't have one.
  const teamAnswerableScopeState = useTeamAnswerableScopeState(
    teamId,
    hasAnswer ? puzName : null
  );
  const a13y = teamAnswerableScopeState?.approachability ?? null;

  if (
    teamId === null ||
    puzName === null ||
    hintScopeState === null ||
    displayName === null
  )
    return <Spinner inline={true} />;

  return (
    <div className="admin-queue-task-summary-hint">
      <h4>
        Hint Request: {displayName}{" "}
        {a13y === null ? null : (
          <div style={{ display: "inline-block" }}>
            <Approachability approachability={a13y} tooltip />
          </div>
        )}
      </h4>
      <TeamInfo teamId={teamId} />
      {/*
      <div className="admin-queue-task-puzzle-preview-wrapper">
        <div className="admin-queue-task-puzzle-preview">
          <PuzzleContentPreviewWithLink puzName={puzName} />
        </div>
      </div>
      */}
      <HintDisplay hintId={hintId} hint={hintScopeState} isAdmin={true} />
    </div>
  );
}

function CollapsibleContent(
  props: PropsWithChildren<{
    headerContent: ReactNode;
  }>
) {
  const { headerContent, children } = props;
  return (
    <details>
      <summary>
        <h4 style={{ display: "inline" }}>{headerContent}</h4>
      </summary>
      {children}
    </details>
  );
}

const HintAdminQueueTaskContext = createContext<{
  setAnswerResponse: (response: string) => void;
} | null>(null);

export const useHintAdminQueueTaskContext = () =>
  useContext(HintAdminQueueTaskContext);

const HintAdminQueueTaskContextProvider = (
  props: PropsWithChildren<{
    setAnswerResponse: (response: string) => void;
  }>
) => {
  const { children, setAnswerResponse } = props;
  return (
    <HintAdminQueueTaskContext.Provider value={{ setAnswerResponse }}>
      <div className="admin-queue-task-hint">{children}</div>
    </HintAdminQueueTaskContext.Provider>
  );
};

export function HintAdminQueueTaskDisplay(props: {
  taskId: string;
  hintId: string;
  isClaimedBySelf: boolean;
}) {
  const { taskId, hintId, isClaimedBySelf } = props;
  const hintScopeState = useScopeStateResolvable(ScopeType.HINT, { hintId });
  const { puzName = null, teamId = null } = hintScopeState ?? {};
  const displayName = usePuzzleDisplayName(puzName, teamId);
  const sortedHints = useSortedHintsForTeamPuzzle(teamId, puzName);
  const huntAdminScopeState = useGlobalScopeState(ScopeType.HUNT_ADMIN);
  const hasAnswer =
    (puzName !== null &&
      (huntAdminScopeState?.puzzles[puzName].answer ?? null) !== null) ??
    false;
  // Don't try to sync an answerable state if the puzzle doesn't have one.
  const teamAnswerableScopeState = useTeamAnswerableScopeState(
    teamId,
    hasAnswer ? puzName : null
  );
  const sockClient = useSockClient();
  const setRedirectRoute = useNavStore((state) => state.setRedirectRoute);
  const responseInputRef = useRef<HTMLTextAreaElement>(null);
  const [responseType, setResponseType] = useState<HintResponseType | null>(
    null
  );
  const puzzleOtherHintsFilter = useCallback(
    ([otherTaskId, task]: [string, AdminQueueTaskSummary]) =>
      taskId !== otherTaskId &&
      puzName !== null &&
      task.type === AdminQueueTaskType.HINT &&
      task.puzName === puzName,
    [taskId, puzName]
  );
  const teamOtherHintsFilter = useCallback(
    ([otherTaskId, task]: [string, AdminQueueTaskSummary]) =>
      taskId !== otherTaskId &&
      teamId !== null &&
      task.type === AdminQueueTaskType.HINT &&
      task.teamId === teamId,
    [taskId, teamId]
  );
  const otherHintsNumPerPage = 5;

  useEffect(() => {
    if (hintScopeState === null) return;
    setResponseType(hintScopeState.response?.type ?? null);
  }, [hintScopeState]);

  if (
    teamId === null ||
    puzName === null ||
    huntAdminScopeState === null ||
    hintScopeState === null ||
    displayName === null ||
    (hasAnswer && teamAnswerableScopeState === null) ||
    sortedHints === null
  )
    return <Spinner inline={true} />;
  const hintIndex = sortedHints.findIndex(
    ([otherHintId, otherHint]) => otherHintId === hintId
  );
  // If the hint hasn't synced yet, wait for it.
  if (hintIndex < 0) return <Spinner inline={true} />;
  const [, hint] = sortedHints[hintIndex];

  const { cols = 1, rows = 1 } = huntAdminScopeState.puzzles[puzName] ?? {};
  const hasPreviousGuesses =
    Object.keys(teamAnswerableScopeState?.submissions ?? {}).length > 0;
  const a13y = teamAnswerableScopeState?.approachability ?? null;

  return (
    <HintAdminQueueTaskContextProvider
      setAnswerResponse={(response) => {
        if (!responseType) {
          setResponseType(HintResponseType.ANSWERED);
          return;
        }
        if (responseInputRef.current) {
          responseInputRef.current.focus();
          responseInputRef.current.value = response;
        }
      }}
    >
      <div>
        <h2>
          Hint Request: {displayName}{" "}
          {a13y === null ? null : (
            <div style={{ display: "inline-block" }}>
              <Approachability approachability={a13y} tooltip />
            </div>
          )}
        </h2>
        <TeamInfo teamId={teamId} />
        <CollapsibleContent
          headerContent={
            <>
              Puzzle{" "}
              <PuzzleLink
                puzName={puzName}
                subpage="puzzle"
                button
                target="_blank"
              >
                Open
              </PuzzleLink>
            </>
          }
        >
          <div
            style={{
              aspectRatio: `${cols} / ${rows}`,
              position: "relative",
            }}
          >
            <PuzzleContentDisplay puzName={puzName} />
          </div>
        </CollapsibleContent>
        <CollapsibleContent
          headerContent={
            <>
              Solution{" "}
              <PuzzleLink
                puzName={puzName}
                subpage="solution"
                button
                target="_blank"
              >
                Open
              </PuzzleLink>
            </>
          }
        >
          <PuzzleContentDisplay
            puzName={puzName}
            contentType={PuzzleContentType.SOLUTION}
          />
        </CollapsibleContent>
        {/*
      <div>
        <h4>Canned hints</h4>
        <AdminCannedHintsDisplay puzName={puzName} teamId={teamId} />
      </div>
      */}
        <CollapsibleContent headerContent="Pieces and feeders">
          <AdminRelatedPuzzleControls
            puzName={puzName}
            teamId={teamId}
            readOnly
          />
        </CollapsibleContent>
        {teamAnswerableScopeState !== null ? (
          <div>
            {!hasPreviousGuesses ? (
              <>
                <h4>Previous guesses</h4>
                <div>
                  The team has not made any guesses yet for this puzzle.
                </div>
              </>
            ) : (
              <PreviousGuessesDisplay teamId={teamId} puzName={puzName} />
            )}
          </div>
        ) : null}
        <CollapsibleContent headerContent="Other hints for this puzzle">
          <AdminQueueTaskListDisplay
            taskFilter={puzzleOtherHintsFilter}
            numTasksPerPage={otherHintsNumPerPage}
          />
        </CollapsibleContent>
        <CollapsibleContent headerContent="Other hints for this team">
          <AdminQueueTaskListDisplay
            taskFilter={teamOtherHintsFilter}
            numTasksPerPage={otherHintsNumPerPage}
          />
        </CollapsibleContent>
        <hr className="show-only-when-small" />
      </div>
      <div>
        {sortedHints.slice(0, hintIndex).map(([otherHintId, otherHint]) => (
          <HintDisplay
            key={otherHintId}
            hintId={otherHintId}
            hint={otherHint}
            isAdmin={true}
          />
        ))}
        <HintDisplay
          key={hintIndex}
          hintId={hintId}
          hint={hint}
          isAdmin={true}
        />
        <hr />
        {!isClaimedBySelf ? (
          <div className="admin-queue-claim-this-task-notice">
            Claim this task to respond to it.
          </div>
        ) : null}
        <div className="admin-queue-hint-status">
          Status:{" "}
          <div className="admin-queue-task-status-tabs">
            <ButtonTabs
              tabs={[
                { content: "No response", value: null, disabled: true },
                { content: "Answered", value: HintResponseType.ANSWERED },
                {
                  content: hintScopeState?.request?.isFollowup
                    ? "Thread closed"
                    : "Refunded",
                  value: HintResponseType.REFUNDED,
                },
                {
                  content: "Obsolete",
                  value: HintResponseType.OBSOLETE,
                  disabled: true,
                },
              ]}
              selected={responseType}
              setSelected={setResponseType}
              disabled={!isClaimedBySelf}
            />
          </div>
        </div>
        {responseType !== null ? (
          <>
            <textarea
              className="hint-text-area"
              disabled={!isClaimedBySelf}
              defaultValue={hintScopeState.response?.content ?? ""}
              ref={responseInputRef}
            />
            <button
              disabled={!isClaimedBySelf}
              onClick={() => {
                (async () => {
                  if (responseType === null) return;
                  if (responseInputRef.current === null) return;
                  if (responseInputRef.current.value === "") {
                    toastr.warning("Response required.");
                    return;
                  }
                  await sockClient.sendScopeStepAndWaitAsync(
                    {
                      type: ScopeType.HINT,
                      hintId,
                    },
                    {
                      setResponse: {
                        type: responseType,
                        content: responseInputRef.current.value,
                      },
                    }
                  );
                  toastr.success("Response submitted.");
                  setRedirectRoute({ route: "admin_queue" });
                })().catch(console.error);
              }}
            >
              <Twemoji>
                {((): string => {
                  switch (responseType) {
                    case HintResponseType.ANSWERED:
                      return "Mark hint as answered";
                    case HintResponseType.REFUNDED:
                      if (hintScopeState?.request?.isFollowup)
                        return "🛑 Close thread";
                      return "♻️ Refund hint";
                    case HintResponseType.OBSOLETE:
                      return "Already obsolete!";
                  }
                })()}
              </Twemoji>
            </button>
          </>
        ) : null}
      </div>
    </HintAdminQueueTaskContextProvider>
  );
}
